import { Component, OnInit, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core'
import {
  Inquiry,
  isBdcRtbRequest,
  isBookingFromAirbnb,
  isInquiryFromAirbnb,
  isReadOnlyBooking,
} from '@tv3/store/inquiry/inquiry.model'
import { Store, select } from '@ngrx/store'
import { isCancelingBooking, isConfirmingBooking } from '@tv3/store/inquiry/inquiry.selectors'
import {
  holiduCancelReasons,
  AirbnbChannelService,
  AirbnbReservationAlterationResponse,
  isPendingAirbnbAlteration,
  isPendingVRBOAlteration,
  VRBOReservationAlterationItem,
} from '@tokeet-frontend/channels'
import {
  Destroyable,
  ConfirmDialogService,
  Toaster,
  Rental,
  selectRentalById,
  untilDestroy,
  isSomething,
} from '@tokeet-frontend/tv3-platform'
import { finalize, switchMap, filter, map } from 'rxjs/operators'
import {
  CancelBooking,
  ConfirmBooking,
  InquiryIsUpdated,
  InquiryUpdateComplete,
  CancelBookingWithReason,
} from '@tv3/store/inquiry/inquiry-fields.actions'
import { isChannelCancelable, isVrboBooking } from '@tokeet-frontend/inquiry'
import { DeclineAirbnbBookingDialogService } from '@tv3/containers/inquiries/overlays/decline-airbnb-booking-dialog/decline-aibrnb-booking-dialog.service'
import { CancelAirbnbBookingDialogService } from '@tv3/containers/inquiries/overlays/cancel-airbnb-booking-dialog/cancel-airbnb-booking-dialog.service'
import { BehaviorSubject } from 'rxjs'
import { Actions, ofType } from '@ngrx/effects'
import { BookingAlterationChanged } from '@tv3/store/inquiry/inquiry.actions'
import { AirbnbCreateAlterationDialogService } from '../../airbnb-create-alteration-dialog/airbnb-create-alteration-dialog.service'
import { AirbnbReservationAlterationDialogService } from '../../airbnb-reservation-alteration-dialog/airbnb-reservation-alteration-dialog.service'
import moment from 'moment'
import { VrboReservationAlterationDialogService } from '../../vrbo-reservation-alteration-dialog/vrbo-reservation-alteration-dialog.service'
import { AmplitudeService } from '@tv3/services/amplitude.service'

@Component({
  selector: 'app-book-btn',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  templateUrl: './book-btn.component.html',
  styleUrls: ['./book-btn.component.scss'],
})
export class BookBtnComponent extends Destroyable implements OnInit, OnChanges {
  _inquiry: Inquiry
  get inquiry() {
    return this._inquiry
  }
  @Input() set inquiry(inquiry: Inquiry) {
    this._inquiry = inquiry
    this.init()
  }

  @Input() isLoading = false
  @Input() btnSm = false

  rental: Rental

  airbnbAlteration: AirbnbReservationAlterationResponse
  isAirbnbAlterationInitiated = false
  isAirbnbAPIBooking = false
  isAirbnbAPIInquiry = false
  isAirbnbListingLinked = false

  isAirbnbChangeable = false
  bookingStatus: string | null = null
  isBooked = false
  isCanceled = false
  isReadOnlyBooking = false
  isVrbo = false
  isVrboAlterationInitiated = false
  isHolidu = false
  // TV4-6322: a Booking.com request is approved in the request panel; confirming it locally would
  // create a booking Booking.com never made.
  isBdcRtbRequest = false

  isCanceling$ = this.store.pipe(select(isCancelingBooking))
  isConfirmingBooking$ = this.store.pipe(select(isConfirmingBooking))

  holiduCancelReasons = holiduCancelReasons

  rentalId$ = new BehaviorSubject<string>(null)
  constructor(
    private actions: Actions,
    private store: Store<any>,
    private toaster: Toaster,
    private confirmDialog: ConfirmDialogService,
    private airbnbChannelService: AirbnbChannelService,
    private declineAirbnbBookingDialog: DeclineAirbnbBookingDialogService,
    private cancelAirbnbBookingDialog: CancelAirbnbBookingDialogService,
    private airbnbCreateAlterationDialog: AirbnbCreateAlterationDialogService,
    private airbnbReservationAlterationDialog: AirbnbReservationAlterationDialogService,
    private vrboAlterationDialog: VrboReservationAlterationDialogService,
    private amplitudeService: AmplitudeService
  ) {
    super()
  }

  ngOnInit(): void {
    this.rentalId$
      .pipe(
        filter((rentalId) => !!rentalId),
        switchMap((rentalId) => this.store.pipe(select(selectRentalById(rentalId)))),
        untilDestroy(this)
      )
      .subscribe((rental) => {
        this.rental = rental
        this.isAirbnbListingLinked = !!rental?.abb_listing_id
      })

    this.actions.pipe(ofType(BookingAlterationChanged), untilDestroy(this)).subscribe((action) => {
      if (action.source === 'airbnb') {
        this.tryToLoadAirbnbAlteration(action.alterationId)
      }
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.inquiry?.currentValue) {
      this.rentalId$.next(this.inquiry.rentalId)
    }
  }

  init() {
    this.bookingStatus = this.inquiry?.status ?? null

    this.isAirbnbAPIBooking = isBookingFromAirbnb(this.inquiry)
    this.isAirbnbAPIInquiry = isInquiryFromAirbnb(this.inquiry)
    this.isAirbnbChangeable =
      this.isAirbnbAPIBooking &&
      this.bookingStatus === 'accept' &&
      this.inquiry.guestDepart > moment.utc().startOf('day').unix()
    this.isBooked = !!this.inquiry?.booked
    this.isCanceled = !!this.inquiry?.canceled
    this.isReadOnlyBooking = this.inquiry?.inquirySource === 'marriott'
    this.isVrbo = isVrboBooking(this.inquiry)
    this.isHolidu = /holidu/.test(this.inquiry?.inquirySource)
    this.isBdcRtbRequest = isBdcRtbRequest(this.inquiry)
    if (this.isAirbnbAPIBooking) this.tryToLoadAirbnbAlteration(this.inquiry?.attributes?.alteration_requested)
    if (this.isVrbo && this.inquiry?.alteration_requested)
      this.isVrboAlterationInitiated = isPendingVRBOAlteration(
        this.inquiry?.alteration_requested as VRBOReservationAlterationItem
      )
  }

  tryToLoadAirbnbAlteration(alterationId: string) {
    if (!alterationId) {
      this.airbnbAlteration = null
      this.isAirbnbAlterationInitiated = false
      return
    }
    this.airbnbChannelService.getReservationAlterationById(alterationId).subscribe((alteration) => {
      this.isAirbnbAlterationInitiated = isPendingAirbnbAlteration(alteration)
      this.airbnbAlteration = this.isAirbnbAlterationInitiated ? alteration : null
    })
  }

  onAirbnbAlteration() {
    this.amplitudeService.logEvent('inquiry-overlay-airbnb-alteration')
    if (!this.airbnbAlteration) {
      this.airbnbCreateAlterationDialog.open(this.inquiry)
    } else {
      this.airbnbReservationAlterationDialog.open({ inquiry: this.inquiry, alteration: this.airbnbAlteration })
    }
  }

  onVrboAlteration() {
    this.vrboAlterationDialog.open(this.inquiry)
  }

  onConfirmBooking() {
    this.amplitudeService.logEvent('inquiry-overlay-confirm-booking')
    this.store.dispatch(ConfirmBooking({ inquiry: this.inquiry }))
  }

  onAcceptAirbnbBooking() {
    this.amplitudeService.logEvent('inquiry-overlay-accept-airbnb')
    this.isLoading = true
    this.airbnbChannelService
      .acceptBooking(this.inquiry.inquiryId)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        () => {
          this.toaster.success($localize`Booking accepted successfully.`)
          this.store.dispatch(InquiryUpdateComplete({ update: { id: this.inquiry.id, changes: { status: 'accept' } } }))
          this.store.dispatch(InquiryIsUpdated({ update: { id: this.inquiry.id, changes: { status: 'accept' } } }))
        },
        (error) => {
          this.toaster.error($localize`Unable to accept booking`, '', error)
        }
      )
  }

  onDeclineAirbnbBooking() {
    this.amplitudeService.logEvent('inquiry-overlay-decline-airbnb')
    this.declineAirbnbBookingDialog.open(this.inquiry, this.rental)
  }

  onCancelAirbnbBooking() {
    this.amplitudeService.logEvent('inquiry-overlay-cancel-airbnb')
    this.cancelAirbnbBookingDialog.open(this.inquiry, this.rental)
  }

  onCancelBooking(byGuest = false) {
    this.amplitudeService.logEvent('inquiry-overlay-cancel-booking')
    if (this.isVrbo) {
      this.confirmDialog
        .confirmExtra({
          title: $localize`Action required in VRBO`,
          body: $localize`AdvanceCM cannot cancel this reservation in VRBO. Cancel it in VRBO before continuing.`,
          extra: $localize`I have cancelled this reservation in VRBO.`,
          extraCheckedRequired: true,
          extraCheckedRequiredMsg: $localize`Confirm that the reservation has been cancelled in VRBO to continue.`,
          confirmText: $localize`Mark cancelled in AdvanceCM`,
          cancelText: $localize`Go back`,
          isDangerous: true,
        })
        .subscribe(() => {
          this.store.dispatch(CancelBooking({ inquiry: this.inquiry, byGuest }))
        })
      return
    }

    const additional = isChannelCancelable(this.inquiry.inquirySource)
      ? ''
      : $localize`Please note that this will only affect the booking in AdvanceCM and cancellation will be necessary on the originating channel.`
    this.confirmDialog
      .confirm({
        title: $localize`Cancel booking?`,
        body: $localize`Are you sure you want to cancel this booking? ` + additional,
      })
      .subscribe(() => {
        this.store.dispatch(CancelBooking({ inquiry: this.inquiry, byGuest }))
      })
  }

  onCancelBookingWithReason(reason: string) {
    this.amplitudeService.logEvent('inquiry-overlay-cancel-booking-reason')
    const additional = isChannelCancelable(this.inquiry.inquirySource)
      ? ''
      : $localize`Please note that this will only affect the booking in AdvanceCM and cancellation will be necessary on the originating channel.`
    this.confirmDialog
      .confirm({
        title: $localize`Cancel booking?`,
        body: $localize`Are you sure you want to cancel this booking? ` + additional,
      })
      .subscribe(() => {
        this.store.dispatch(CancelBookingWithReason({ inquiry: this.inquiry, reason }))
      })
  }
}
